import { DATA_TYPE, IColumn, ITable } from 'jsstore';
import {
	DefinitionEntityState,
	DataTypeState,
	PropertyState,
	BUILT_IN_BASE_ENTITY_IDS,
	BaseEntityNames,
	EntityType,
	PrimitiveTypes,
	resolveEntityName,
	toCamelCase,
	toPascalCase,
	getColumnProperties,
	searchStatementUtils,
	capitalizeFirstLetter,
	lowercaseFirstLetter,
	checkIsNullable,
	selectFinalSingleNotNullDataTypeFromOrGroup,
	resolveDataTypeLabel
} from '@elyx-code/project-logic-tree';
import {
	ITableColumnUI,
	ITableRow,
	TableColumnType
} from '../../lib/table/types';

// Turns an instance of DataTypeState into a jsstore DATA_TYPE definition
export function dataTypeStateToColumnDataType(
	dataType: DataTypeState | null
): DATA_TYPE {
	if (!dataType) {
		return DATA_TYPE.String;
	}

	const finalDT = selectFinalSingleNotNullDataTypeFromOrGroup(dataType);

	if (!!finalDT.foreignKeyRef) {
		const foreignKeyDt = finalDT.foreignKeyRef.getDataType(null);

		return dataTypeStateToColumnDataType(foreignKeyDt);
	}

	// If the 'entity' of the data type is of type primitive entity,
	// then map to primitive types
	if (finalDT.entity?.type === EntityType.PrimitiveEntity) {
		switch (finalDT.entity.name) {
			case PrimitiveTypes.String:
				return DATA_TYPE.String;
			case PrimitiveTypes.Number:
				return DATA_TYPE.Number;
			case PrimitiveTypes.Boolean:
				return DATA_TYPE.Boolean;
			case PrimitiveTypes.Date:
				return DATA_TYPE.DateTime;
			default:
				return DATA_TYPE.String;
		}
	} else if (finalDT.entity?.type === EntityType.DefinitionEntity) {
		const columnProperties = getColumnProperties(
			finalDT.entity as DefinitionEntityState
		);
		const builtInPrimaryKeyPropertyId =
			BUILT_IN_BASE_ENTITY_IDS[EntityType.BuiltInBaseEntity][
				BaseEntityNames.PERSISTED_ENTITY
			].properties.primaryKey.id;

		const pkProperty = columnProperties.find(
			(p) =>
				p.id === builtInPrimaryKeyPropertyId ||
				!!p.implements.find(
					(impl) => impl.id === builtInPrimaryKeyPropertyId
				)
		);

		if (pkProperty) {
			return dataTypeStateToColumnDataType(pkProperty.dataType);
		}
	}

	return DATA_TYPE.String;
}

export function columnDefTypeToTransferType(
	dataType: DATA_TYPE
): TableColumnType {
	switch (dataType) {
		case DATA_TYPE.String:
			return TableColumnType.String;
		case DATA_TYPE.Number:
			return TableColumnType.Number;
		case DATA_TYPE.Boolean:
			return TableColumnType.Boolean;
		case DATA_TYPE.DateTime:
			return TableColumnType.Date;
		default:
			return TableColumnType.String;
	}
}

export function resolveColumnTypeFromDataTypeEntity(
	dataType: DataTypeState
): TableColumnType {
	if (!dataType) {
		return TableColumnType.String;
	}

	const finalDT = selectFinalSingleNotNullDataTypeFromOrGroup(dataType);

	if (!!finalDT.foreignKeyRef) {
		const foreignKeyDt = finalDT.foreignKeyRef.getDataType(null);

		return resolveColumnTypeFromDataTypeEntity(foreignKeyDt);
	}

	if (!finalDT.entity) {
		return TableColumnType.String;
	}

	// If the 'entity' of the data type is of type primitive entity,
	// then map to primitive types
	// Otherwise, it is a foreign key reference to an entity, so we map to string
	if (finalDT.entity?.type === EntityType.PrimitiveEntity) {
		switch (finalDT.entity.name) {
			case PrimitiveTypes.String:
				return TableColumnType.String;
			case PrimitiveTypes.Number:
				return TableColumnType.Number;
			case PrimitiveTypes.Boolean:
				return TableColumnType.Boolean;
			case PrimitiveTypes.Date:
				return TableColumnType.Date;
			case PrimitiveTypes.UUID:
				return TableColumnType.UUID;
			default:
				return TableColumnType.String;
		}
	} else if (finalDT.entity?.type === EntityType.DefinitionEntity) {
		const columnProperties = getColumnProperties(
			finalDT.entity as DefinitionEntityState
		);
		const builtInPrimaryKeyPropertyId =
			BUILT_IN_BASE_ENTITY_IDS[EntityType.BuiltInBaseEntity][
				BaseEntityNames.PERSISTED_ENTITY
			].properties.primaryKey.id;

		const pkProperty = columnProperties.find(
			(p) =>
				p.id === builtInPrimaryKeyPropertyId ||
				!!p.implements.find(
					(impl) => impl.id === builtInPrimaryKeyPropertyId
				)
		);

		if (pkProperty) {
			return resolveColumnTypeFromDataTypeEntity(pkProperty.dataType);
		}
	}

	return TableColumnType.String;
}

export function resolveForeignKeyInfo(
	dataType: DataTypeState | null
):
	| {
			label: string;
			foreignKey: { table: string; key: string };
			entity: DefinitionEntityState;
	  }
	| undefined {
	if (!dataType) return undefined;

	const finalDT = selectFinalSingleNotNullDataTypeFromOrGroup(dataType);

	if (!!finalDT.foreignKeyRef) {
		const targetProperty = finalDT.foreignKeyRef as PropertyState;
		const targetEntity = targetProperty.parent as DefinitionEntityState;

		if (targetEntity && targetProperty) {
			const tableName = targetEntity.name;
			const columnName = getColumnNameForProperty(targetProperty);
			return {
				label: `→ ${tableName}.${columnName}`,
				foreignKey: { table: tableName, key: columnName },
				entity: targetEntity
			};
		}
	}

	if (finalDT.entity?.type === EntityType.DefinitionEntity) {
		const columnProperties = getColumnProperties(
			finalDT.entity as DefinitionEntityState
		);
		const builtInPrimaryKeyPropertyId =
			BUILT_IN_BASE_ENTITY_IDS[EntityType.BuiltInBaseEntity][
				BaseEntityNames.PERSISTED_ENTITY
			].properties.primaryKey.id;

		const pkProperty = columnProperties.find(
			(p) =>
				p.id === builtInPrimaryKeyPropertyId ||
				!!p.implements.find(
					(impl) => impl.id === builtInPrimaryKeyPropertyId
				)
		);

		if (pkProperty) {
			const targetEntity = finalDT.entity as DefinitionEntityState;
			const pkColumnName = getColumnNameForProperty(pkProperty);
			return {
				label: `→ ${targetEntity.name}.${pkColumnName}`,
				foreignKey: {
					table: targetEntity.name,
					key: pkColumnName
				},
				entity: targetEntity
			};
		}
	}

	return undefined;
}

export function getColumnNameForProperty(property: PropertyState): string {
	return (
		property.codeName ||
		lowercaseFirstLetter(
			toCamelCase(resolveEntityName(property, property.project))
		)
	);
}

export function propertyStateToColumn(property: PropertyState): IColumn {
	const columnName = getColumnNameForProperty(property);

	const builtInPrimaryKeyPropertyId =
		BUILT_IN_BASE_ENTITY_IDS[EntityType.BuiltInBaseEntity][
			BaseEntityNames.PERSISTED_ENTITY
		].properties.primaryKey.id;

	const isPrimaryKey =
		property.id === builtInPrimaryKeyPropertyId ||
		!!property.implements.find(
			(impl) => impl.id === builtInPrimaryKeyPropertyId
		);

	const column: IColumn = {
		name: columnName,
		notNull: !checkIsNullable(property.dataType),
		primaryKey: isPrimaryKey,
		unique: isPrimaryKey || property.unique,
		dataType: dataTypeStateToColumnDataType(property.dataType),
		default: property.getDefaultValue()?.value
	};

	return column;
}

export function fromColumnInstanceToColumnTransfer(
	column: searchStatementUtils.IColumn
): ITableColumnUI {
	let label = column.columnName;

	if (column.property) {
		label = resolveEntityName(column.property, column.property.project);
	}

	const fkInfo = column.property
		? resolveForeignKeyInfo(column.property.dataType)
		: undefined;

	return {
		// Find entity property that matches the column name and resolve the property's name
		label: label,
		hidden: false,
		value: column.columnName,
		sort: null,
		editable: true,
		sortable: true,
		searchable: true,
		size: 'lg',
		type: resolveColumnTypeFromDataTypeEntity(column.property.dataType),
		primaryKey: column.primaryKey,
		dataTypeLabel: fkInfo?.label,
		foreignKey: fkInfo?.foreignKey || null,
		nullable: column.property
			? checkIsNullable(column.property.dataType)
			: true
	};
}

export function fromJsstoreColumnToColumnTransfer(
	column: IColumn,
	tableEntity: DefinitionEntityState
): ITableColumnUI {
	const columnProperties = getColumnProperties(tableEntity);
	const property = columnProperties.find(
		(property) =>
			property.codeName ||
			property.extends?.codeName ||
			property.implements[0]?.codeName ||
			toCamelCase(property.name) === column.name
	);

	let label = column.name;

	if (property) {
		label = resolveEntityName(property, property.project);
	}

	const fkInfo = property
		? resolveForeignKeyInfo(property.dataType)
		: undefined;

	return {
		// Find entity property that matches the column name and resolve the property's name
		label: label,
		hidden: false,
		value: column.name,
		sort: null,
		editable: true,
		sortable: true,
		searchable: true,
		size: 'lg',
		type: resolveColumnTypeFromDataTypeEntity(
			property ? property.dataType : null
		),
		primaryKey: column.primaryKey,
		dataTypeLabel: fkInfo?.label,
		foreignKey: fkInfo?.foreignKey || null,
		nullable: property
			? checkIsNullable(property.dataType)
			: !column.notNull
	};
}

export function toRowTransfer(row: { [columnName: string]: any }): ITableRow {
	// Turns an object with keys into a list of objects in the same order, all with a property called 'value'
	return {
		data: Object.keys(row).map((columnName) => ({
			value: row[columnName]
		}))
	};
}

export function persistedDefinitionEntityToDBTableConfig(
	entity: DefinitionEntityState
): { definition: ITable; entity: DefinitionEntityState } {
	const definition: ITable = {
		name: entity.id,
		columns: {}
	};

	const columnProperties: PropertyState[] = getColumnProperties(entity);

	// Loop over properties and add one key for each in the 'columns' property of the table
	columnProperties.forEach((property) => {
		const column = propertyStateToColumn(property);

		definition.columns[column.name] = column;
	});

	return { definition, entity };
}

export function createQueryErrorMessage(error: any, tables: any[]): string {
	let message =
		error?.response?.data?.message ||
		error?.response?.message ||
		error?.message ||
		'Something went wrong';

	if (message.includes('SequelizeForeignKeyConstraintError')) {
		const tableName = message.split('on table "')[2].split('"')[0];

		const table = tables.find(
			(table: any) => table.tableName === tableName
		);

		message = `This row is referenced by an entry on ${
			table?.entity?.name
				? 'the ' + table?.entity?.name + ' table'
				: 'another table'
		}. Delete the row from that table first.`;

		return message;
	}

	message = message.replace('Something went wrong: ', '');
	message = message.replace(
		'SequelizeUniqueConstraintError: ',
		'Field should be unique. '
	);
	message = message.replace('SequelizeForeignKeyConstraintError: ', '');
	message = message.replace('SequelizeDatabaseError: ', '');
	message = message.replace('SequelizeValidationError: ', '');

	message = capitalizeFirstLetter(message);

	return message;
}
